import React from "react";

/**
 * Minimal markdown renderer for walkthrough prose.
 * Supports: headings (## ###), paragraphs, **bold**, `inline code`,
 * ```fenced code blocks```, and `-`/`1.` lists.
 *
 * Deliberately tiny — no new dependencies. If we ever need tables, links,
 * images, or task lists, swap in a real parser.
 */

type Block =
	| { type: "heading"; level: 2 | 3; text: string }
	| { type: "paragraph"; text: string }
	| { type: "code"; lang: string; text: string }
	| { type: "list"; ordered: boolean; items: string[] };

function parseBlocks(src: string): Block[] {
	const lines = src.replace(/\r\n/g, "\n").split("\n");
	const blocks: Block[] = [];
	let i = 0;

	while (i < lines.length) {
		const line = lines[i];

		// Fenced code block
		if (line.trimStart().startsWith("```")) {
			const lang = line.trim().slice(3).trim();
			const buf: string[] = [];
			i++;
			while (i < lines.length && !lines[i].trimStart().startsWith("```")) {
				buf.push(lines[i]);
				i++;
			}
			// skip closing fence
			if (i < lines.length) i++;
			blocks.push({ type: "code", lang, text: buf.join("\n") });
			continue;
		}

		// Heading
		const hMatch = line.match(/^(#{2,3})\s+(.+)$/);
		if (hMatch) {
			blocks.push({
				type: "heading",
				level: hMatch[1].length as 2 | 3,
				text: hMatch[2],
			});
			i++;
			continue;
		}

		// Blank line
		if (line.trim() === "") {
			i++;
			continue;
		}

		// List
		const isBullet = /^\s*[-*]\s+/.test(line);
		const isNumbered = /^\s*\d+\.\s+/.test(line);
		if (isBullet || isNumbered) {
			const ordered = isNumbered;
			const items: string[] = [];
			while (
				i < lines.length &&
				(ordered ? /^\s*\d+\.\s+/.test(lines[i]) : /^\s*[-*]\s+/.test(lines[i]))
			) {
				items.push(
					lines[i].replace(ordered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/, ""),
				);
				i++;
			}
			blocks.push({ type: "list", ordered, items });
			continue;
		}

		// Paragraph — collect until blank line, heading, list, or fence
		const buf: string[] = [];
		while (
			i < lines.length &&
			lines[i].trim() !== "" &&
			!/^(#{2,3})\s+/.test(lines[i]) &&
			!/^\s*[-*]\s+/.test(lines[i]) &&
			!/^\s*\d+\.\s+/.test(lines[i]) &&
			!lines[i].trimStart().startsWith("```")
		) {
			buf.push(lines[i]);
			i++;
		}
		blocks.push({ type: "paragraph", text: buf.join(" ") });
	}

	return blocks;
}

// Inline: **bold** and `code`. Order matters — split on code first so backticks
// can't be eaten by bold matching.
function renderInline(text: string, keyBase: string): React.ReactNode[] {
	const out: React.ReactNode[] = [];
	const codeRe = /`([^`]+)`/g;
	let lastIndex = 0;
	let match: RegExpExecArray | null;
	let idx = 0;

	while ((match = codeRe.exec(text)) !== null) {
		if (match.index > lastIndex) {
			out.push(
				...renderBold(text.slice(lastIndex, match.index), `${keyBase}-${idx++}`),
			);
		}
		out.push(
			<code
				key={`${keyBase}-code-${idx++}`}
				className="px-1 py-0.5 rounded bg-gray-100 dark:bg-white/[0.06] text-[12px] font-mono text-orange-700 dark:text-amber-300"
			>
				{match[1]}
			</code>,
		);
		lastIndex = match.index + match[0].length;
	}
	if (lastIndex < text.length) {
		out.push(
			...renderBold(text.slice(lastIndex), `${keyBase}-tail-${idx++}`),
		);
	}
	return out;
}

function renderBold(text: string, keyBase: string): React.ReactNode[] {
	const parts = text.split(/(\*\*[^*]+\*\*)/g);
	return parts.map((p, idx) => {
		if (p.startsWith("**") && p.endsWith("**")) {
			return (
				<strong
					key={`${keyBase}-b-${idx}`}
					className="font-semibold text-gray-900 dark:text-gray-100"
				>
					{p.slice(2, -2)}
				</strong>
			);
		}
		return <React.Fragment key={`${keyBase}-t-${idx}`}>{p}</React.Fragment>;
	});
}

interface MarkdownProps {
	source: string;
	className?: string;
}

export const Markdown: React.FC<MarkdownProps> = ({ source, className }) => {
	const blocks = parseBlocks(source);

	return (
		<div className={className}>
			{blocks.map((block, i) => {
				const key = `b-${i}`;
				switch (block.type) {
					case "heading":
						if (block.level === 2) {
							return (
								<h3
									key={key}
									className="text-[14px] font-semibold tracking-tight text-gray-900 dark:text-gray-100 mt-4 mb-1.5 first:mt-0"
								>
									{renderInline(block.text, key)}
								</h3>
							);
						}
						return (
							<h4
								key={key}
								className="text-[12.5px] font-semibold tracking-tight text-gray-800 dark:text-gray-200 mt-3 mb-1 first:mt-0"
							>
								{renderInline(block.text, key)}
							</h4>
						);
					case "paragraph":
						return (
							<p
								key={key}
								className="text-[13px] leading-relaxed text-gray-700 dark:text-gray-300 mb-2.5 last:mb-0"
							>
								{renderInline(block.text, key)}
							</p>
						);
					case "code":
						return (
							<pre
								key={key}
								className="my-2.5 p-2.5 rounded-md bg-gray-50 dark:bg-white/[0.03] border border-gray-200/80 dark:border-white/[0.06] overflow-x-auto"
							>
								<code className="text-[12px] font-mono text-gray-800 dark:text-gray-200 whitespace-pre">
									{block.text}
								</code>
							</pre>
						);
					case "list": {
						const Tag = block.ordered ? "ol" : "ul";
						return (
							<Tag
								key={key}
								className={`mb-2.5 pl-5 space-y-1 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300 ${block.ordered ? "list-decimal" : "list-disc"}`}
							>
								{block.items.map((item, j) => (
									<li key={`${key}-i-${j}`} className="marker:text-gray-400 dark:marker:text-gray-600">
										{renderInline(item, `${key}-i-${j}`)}
									</li>
								))}
							</Tag>
						);
					}
					default:
						return null;
				}
			})}
		</div>
	);
};
